import type { ReactNode } from "react";
import { routes as publicContentRoutes } from "../../scripts/public-content";
import { Account, AuthSurface } from "./PrivateSurfaces";
import { LandingPage } from "./LandingPage";
import { PublicStoryPage } from "./PublicStoryPage";
import { ReturningCatchV4 } from "./ReturningCatchV4";
import { StartFlow } from "./StartFlow";
import { TodayV3 } from "./TodayV3";
import { EvidenceSurface, MemorySurface, PatternDetailSurface, PatternsSurface, PrivacySettingsSurface, RecordingSettingsSurface, ReflectionSurface, StorySurface } from "./SecondBrainSurfaces";
import { VoiceSettingsSurface } from "./VoiceSettingsSurface";
import { PersonalModelSurface } from "./PersonalModelSurface";

type AppRoute = {
  id: string;
  matches(path: string): boolean;
  render(): ReactNode;
};

const exact = (path: string) => (candidate: string) => candidate === path;
const publicStoryPaths = new Set(publicContentRoutes.map(route => route.path).filter(path => path !== "/"));

export const APP_ROUTES: readonly AppRoute[] = [
  { id: "landing", matches: exact("/"), render: () => <LandingPage/> },
  { id: "start", matches: exact("/start"), render: () => <StartFlow/> },
  { id: "catch", matches: exact("/catch"), render: () => <ReturningCatchV4/> },
  { id: "today", matches: exact("/today"), render: () => <TodayV3/> },
  { id: "model", matches: exact("/model"), render: () => <PersonalModelSurface/> },
  { id: "voice", matches: exact("/voice"), render: () => <VoiceSettingsSurface/> },
  { id: "patterns", matches: exact("/patterns"), render: () => <PatternsSurface/> },
  { id: "pattern-detail", matches: path => /^\/patterns\/[^/]+$/.test(path), render: () => <PatternDetailSurface/> },
  { id: "evidence", matches: exact("/evidence"), render: () => <EvidenceSurface/> },
  { id: "memory", matches: exact("/memory"), render: () => <MemorySurface/> },
  { id: "weekly", matches: exact("/weekly"), render: () => <ReflectionSurface period="weekly"/> },
  { id: "monthly", matches: exact("/monthly"), render: () => <ReflectionSurface period="monthly"/> },
  { id: "story", matches: exact("/my-story"), render: () => <StorySurface/> },
  { id: "recordings", matches: exact("/recordings"), render: () => <RecordingSettingsSurface/> },
  { id: "privacy-settings", matches: exact("/privacy-settings"), render: () => <PrivacySettingsSurface/> },
  { id: "login", matches: exact("/login"), render: () => <AuthSurface mode="login"/> },
  { id: "verify-email", matches: exact("/verify-email"), render: () => <AuthSurface mode="verify-email"/> },
  { id: "reset-password", matches: exact("/reset-password"), render: () => <AuthSurface mode="reset-password"/> },
  { id: "account", matches: exact("/account"), render: () => <Account/> },
  { id: "public-story", matches: path => publicStoryPaths.has(path), render: () => <PublicStoryPage/> },
] as const;

export const APP_ROUTE_CASES = [
  { id: "landing", path: "/" },
  { id: "start", path: "/start" },
  { id: "catch", path: "/catch" },
  { id: "today", path: "/today" },
  { id: "model", path: "/model" },
  { id: "voice", path: "/voice" },
  { id: "patterns", path: "/patterns" },
  { id: "pattern-detail", path: "/patterns/pattern_fixture" },
  { id: "evidence", path: "/evidence" },
  { id: "memory", path: "/memory" },
  { id: "weekly", path: "/weekly" },
  { id: "monthly", path: "/monthly" },
  { id: "story", path: "/my-story" },
  { id: "recordings", path: "/recordings" },
  { id: "privacy-settings", path: "/privacy-settings" },
  { id: "login", path: "/login" },
  { id: "verify-email", path: "/verify-email" },
  { id: "reset-password", path: "/reset-password" },
  { id: "account", path: "/account" },
  ...[...publicStoryPaths].map(path => ({ id: "public-story", path })),
] as const;

export function normalizeAppPath(path: string): string {
  return path.length > 1 ? path.replace(/\/$/, "") : path;
}

export function matchAppRoute(path: string): AppRoute | null {
  const normalized = normalizeAppPath(path);
  const matches = APP_ROUTES.filter(route => route.matches(normalized));
  if (matches.length > 1) throw new Error(`ambiguous_app_route:${normalized}`);
  return matches[0] ?? null;
}
